/**
 * ERGO 360 KOL Section
 * Accordion cards with video playback + Quote switcher
 */

class Ergo360KOL {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    // Entry animation elements
    this.title = container.querySelector('.ergo-360-kol__title');
    this.awardItems = container.querySelectorAll('.ergo-360-kol__award-item');
    this.quotesSection = container.querySelector('.ergo-360-kol__quotes');

    // Accordion elements (Desktop)
    this.accordion = container.querySelector('.ergo-360-kol__accordion');
    this.cards = container.querySelectorAll('.ergo-360-kol__card');
    this.videos = container.querySelectorAll('.ergo-360-kol__video');

    // Mobile elements
    this.mobileScroll = container.querySelector('.ergo-360-kol__mobile-scroll');
    this.mobileCards = container.querySelectorAll('.ergo-360-kol__mobile-card');
    this.mobileVideos = container.querySelectorAll('.ergo-360-kol__mobile-video');
    this.mobileOverlays = container.querySelectorAll('.ergo-360-kol__mobile-overlay');
    this.mobilePosterWraps = container.querySelectorAll('.ergo-360-kol__mobile-poster-wrap');

    // Quote elements
    this.quoteItems = container.querySelectorAll('.ergo-360-kol__quote-item');
    this.quoteBtns = container.querySelectorAll('.ergo-360-kol__quote-btn');
    this.quoteSlider = container.querySelector('.ergo-360-kol__quote-slider');

    // State
    this.currentCardIndex = 0;
    this.currentQuoteIndex = 0;
    this.btnWidth = 170; // 按钮固定宽度
    this.isAnimating = false;
    this.hasInitialized = false;
    this.hasAnimated = false; // 入场动画是否已播放
    this.currentMobileVideoIndex = -1; // 当前播放的移动端视频索引
    this.quoteAutoplayTimer = null; // Quote 自动轮播定时器
    this.quoteAutoplayInterval = 5000; // 5秒自动切换

    this.init();
  }

  init() {
    this.bindCardEvents();
    this.bindMobileVideoEvents();
    this.bindDesktopVideoEndEvents();
    this.bindQuoteEvents();
    this.setupIntersectionObserver();
  }

  /**
   * Bind ended events to desktop videos for auto-switching
   */
  bindDesktopVideoEndEvents() {
    this.videos.forEach((video, index) => {
      video.addEventListener('ended', () => this.handleDesktopVideoEnded(index));
    });
  }

  /**
   * Handle desktop video ended - auto switch to next card
   */
  handleDesktopVideoEnded(index) {
    // 只有当前激活的卡片视频播放完才切换
    if (index !== this.currentCardIndex) return;

    // 计算下一个索引（循环）
    const nextIndex = (index + 1) % this.cards.length;

    // 切换到下一个卡片
    this.switchCard(nextIndex);
  }

  /**
   * Bind click events to mobile video overlays
   */
  bindMobileVideoEvents() {
    this.mobileOverlays.forEach((overlay, index) => {
      overlay.addEventListener('click', () => this.handleMobileVideoClick(index));
    });

    // 视频播放结束时恢复封面
    this.mobileVideos.forEach((video, index) => {
      video.addEventListener('ended', () => this.handleMobileVideoEnded(index));
    });
  }

  /**
   * Handle mobile video click - toggle play/pause
   */
  handleMobileVideoClick(index) {
    const video = this.mobileVideos[index];
    const posterWrap = this.mobilePosterWraps[index];

    if (!video) return;

    // 暂停其他视频
    this.mobileVideos.forEach((v, i) => {
      if (i !== index && !v.paused) {
        v.pause();
        v.style.opacity = '0';
        if (this.mobilePosterWraps[i]) {
          this.mobilePosterWraps[i].style.opacity = '1';
        }
      }
    });

    // 点击时才开始加载视频（严格懒加载）
    if (video.preload === 'none') {
      video.preload = 'auto';
      video.load();
    }

    // 播放当前视频
    video.currentTime = 0;
    video.style.opacity = '1';
    if (posterWrap) {
      posterWrap.style.opacity = '0';
    }

    video.play().catch(() => {
      // Autoplay might be blocked
    });

    // GA4 埋点：KOL 视频播放
    if (window.Ergo360Analytics) {
      const mobileCard = this.mobileCards[index];
      const kolUsername = mobileCard?.querySelector('.ergo-360-kol__mobile-name')?.textContent?.trim() || '';
      window.Ergo360Analytics.kolVideoPlay(kolUsername);
    }

    this.currentMobileVideoIndex = index;
  }

  /**
   * Handle mobile video ended - show poster again
   */
  handleMobileVideoEnded(index) {
    const video = this.mobileVideos[index];
    const posterWrap = this.mobilePosterWraps[index];

    if (video) {
      video.style.opacity = '0';
    }
    if (posterWrap) {
      posterWrap.style.opacity = '1';
    }

    this.currentMobileVideoIndex = -1;
  }

  /**
   * Bind click events to accordion cards
   */
  bindCardEvents() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', () => this.handleCardClick(index));
    });
  }

  /**
   * Handle card click - switch active card
   */
  handleCardClick(index) {
    // Ignore if already active or animating
    if (index === this.currentCardIndex || this.isAnimating) return;

    this.switchCard(index);
  }

  /**
   * Switch to a specific card
   */
  switchCard(index) {
    this.isAnimating = true;

    // Pause current video
    const currentVideo = this.videos[this.currentCardIndex];
    if (currentVideo) {
      currentVideo.pause();
    }

    // GA4 埋点：KOL 卡片展开
    if (window.Ergo360Analytics) {
      const card = this.cards[index];
      const kolUsername = card?.querySelector('.ergo-360-kol__name')?.textContent?.trim() || '';
      window.Ergo360Analytics.kolCardExpand(kolUsername);
    }

    // Update active states
    this.cards.forEach((card, i) => {
      card.dataset.active = (i === index).toString();
    });

    // Update current index
    this.currentCardIndex = index;

    // 预加载目标视频
    const targetVideo = this.videos[index];
    this.preloadVideo(targetVideo);

    // Play new video after transition
    setTimeout(() => {
      if (targetVideo) {
        targetVideo.currentTime = 0;
        targetVideo.play().then(() => {
          // GA4 埋点：KOL 视频播放（桌面端）
          if (window.Ergo360Analytics) {
            const card = this.cards[index];
            const kolUsername = card?.querySelector('.ergo-360-kol__name')?.textContent?.trim() || '';
            window.Ergo360Analytics.kolVideoPlay(kolUsername);
          }
        }).catch(() => {
          // Autoplay might be blocked by browser
        });
      }
      this.isAnimating = false;
    }, 300);
  }

  /**
   * Bind click events to quote navigation buttons
   */
  bindQuoteEvents() {
    this.quoteBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => this.switchQuote(index, true));
    });
  }

  /**
   * Switch to a specific quote
   * @param {number} index - 目标 quote 索引
   * @param {boolean} isUserAction - 是否是用户点击触发的
   */
  switchQuote(index, isUserAction = false) {
    if (index === this.currentQuoteIndex) return;

    // Update quote items
    this.quoteItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });

    // Update buttons
    this.quoteBtns.forEach((btn, i) => {
      btn.classList.toggle('is-active', i === index);
    });

    // 滑动白色背景
    this.moveSlider(index);

    this.currentQuoteIndex = index;

    // 如果是用户点击，重置自动轮播定时器
    if (isUserAction) {
      this.restartQuoteAutoplay();
    }
  }

  /**
   * Start quote autoplay timer
   */
  startQuoteAutoplay() {
    // 如果没有 quote 项，不启动
    if (this.quoteItems.length <= 1) return;

    // 清除已有定时器
    this.stopQuoteAutoplay();

    this.quoteAutoplayTimer = setInterval(() => {
      const nextIndex = (this.currentQuoteIndex + 1) % this.quoteItems.length;
      this.switchQuote(nextIndex, false);
    }, this.quoteAutoplayInterval);
  }

  /**
   * Stop quote autoplay timer
   */
  stopQuoteAutoplay() {
    if (this.quoteAutoplayTimer) {
      clearInterval(this.quoteAutoplayTimer);
      this.quoteAutoplayTimer = null;
    }
  }

  /**
   * Restart quote autoplay timer (用于用户交互后重置)
   */
  restartQuoteAutoplay() {
    this.stopQuoteAutoplay();
    this.startQuoteAutoplay();
  }

  /**
   * Move the slider to the specified index
   */
  moveSlider(index) {
    if (!this.quoteSlider || !this.quoteBtns.length) return;

    // 动态获取按钮宽度以支持响应式
    const btnWidth = this.quoteBtns[0].offsetWidth;
    const translateX = index * btnWidth;
    this.quoteSlider.style.transform = `translateX(${translateX}px)`;
  }

  /**
   * Setup Intersection Observer for entrance animation and video control
   */
  setupIntersectionObserver() {
    // 入场动画 Observer（触发一次，section 顶边进入视窗 50% 位置）
    const entryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.playEntryAnimation();
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -50% 0px' }
    );

    entryObserver.observe(this.container);

    // 可见性 Observer（视频控制、quote轮播）
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 只有在已初始化后才恢复播放
            if (this.hasInitialized) {
              // 恢复当前视频播放
              const currentVideo = this.videos[this.currentCardIndex];
              if (currentVideo) {
                currentVideo.play().catch(() => {});
              }
              // 恢复 quote 自动轮播
              this.startQuoteAutoplay();
            }
          } else {
            // 暂停所有视频
            this.videos.forEach((video) => {
              video.pause();
            });
            // 停止 quote 自动轮播
            this.stopQuoteAutoplay();
          }
        });
      },
      { threshold: 0.1 }
    );

    this.visibilityObserver.observe(this.container);
  }

  /**
   * Play entry animation using GSAP
   */
  playEntryAnimation() {
    // 等待 GSAP 加载
    if (typeof gsap === 'undefined') {
      setTimeout(() => this.playEntryAnimation(), 100);
      return;
    }

    const tl = gsap.timeline();

    // 1. 标题从下淡入
    if (this.title) {
      tl.fromTo(
        this.title,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );
    }

    // 2. KOL Cards 从下淡入（桌面端手风琴 或 移动端横滚）
    const kolCardsElements = [this.accordion, this.mobileScroll].filter(Boolean);
    if (kolCardsElements.length > 0) {
      tl.fromTo(
        kolCardsElements,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' },
        '-=0.4'
      );
    }

    // 3. Award Items stagger 从下淡入
    if (this.awardItems.length > 0) {
      tl.fromTo(
        this.awardItems,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.15 },
        '-=0.3'
      );
    }

    // 4. Quotes Section 从下淡入
    if (this.quotesSection) {
      tl.fromTo(
        this.quotesSection,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          onComplete: () => {
            // 动画完成后初始化功能
            this.initializeSection();
          }
        },
        '-=0.3'
      );
    } else {
      // 如果没有 quotes section，在最后一个动画完成后初始化
      tl.call(() => this.initializeSection());
    }
  }

  /**
   * 预加载视频
   * @param {HTMLVideoElement} video - 视频元素
   */
  preloadVideo(video) {
    if (video && video.preload === 'none') {
      video.preload = 'auto';
      video.load();
    }
  }

  /**
   * Initialize section when it comes into view
   */
  initializeSection() {
    // Set first card as active
    this.cards[0].dataset.active = 'true';
    this.currentCardIndex = 0;

    // 桌面端：预加载并播放第一个视频
    const firstVideo = this.videos[0];
    if (firstVideo && window.innerWidth >= 768) {
      this.preloadVideo(firstVideo);
      setTimeout(() => {
        firstVideo.play().catch(() => {
          // Autoplay might be blocked
        });
      }, 500);
    }
    // 移动端：严格懒加载，不预加载任何视频

    // Set first quote as active
    if (this.quoteItems.length > 0) {
      this.quoteItems[0].classList.add('is-active');
    }
    if (this.quoteBtns.length > 0) {
      this.quoteBtns[0].classList.add('is-active');
    }

    // 启动 quote 自动轮播
    this.startQuoteAutoplay();
  }
}

/**
 * Initialize all KOL sections on the page
 */
function initErgo360KOL() {
  const containers = document.querySelectorAll('.ergo-360-kol');
  containers.forEach((container) => {
    new Ergo360KOL(container);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initErgo360KOL);
} else {
  initErgo360KOL();
}
